// src/App.jsx
import React, { useState, useEffect, useCallback } from "react";
import SummaryCards from "./components/SummaryCards";
import KrMarketSplit from "./components/KrMarketSplit";
import PortfolioTable from "./components/PortfolioTable";
import AllocationChart from "./components/AllocationChart";
import NewsPanel from "./components/NewsPanel";
import SimulationPanel from "./components/SimulationPanel";
import PaperPortfolio from "./components/PaperPortfolio";
import AddHoldingModal from "./components/AddHoldingModal";
import TradesPanel from "./components/TradesPanel";
import PerformancePanel from "./components/PerformancePanel";
import CorrelationPanel from "./components/CorrelationPanel";
import SectorExposure from "./components/SectorExposure";
import PortfolioComment from "./components/PortfolioComment";
import LongShortBoard from "./components/LongShortBoard";
import QuickPairModal from "./components/QuickPairModal";
import RegimeGauge from "./components/RegimeGauge";
import PairSpreadBook from "./components/PairSpreadBook";
import FlowsTab from "./components/FlowsTab";
import { useMarketData } from "./hooks/useMarketData";
import { fetchPortfolio, savePortfolio, addHolding, updateHolding, deleteHolding } from "./hooks/useSheets";
import { samePair, normalizePair, pairId } from "./lib/pairs";

const SHEETS_URL = process.env.REACT_APP_SHEETS_URL;
const IS_CONFIGURED = SHEETS_URL && SHEETS_URL !== "YOUR_GOOGLE_APPS_SCRIPT_URL_HERE";

// Demo data for first-time setup
const DEMO_HOLDINGS = [
  { ticker: "AAPL",  name: "Apple Inc.",     market: "US", side: "Long",  quantity: 100, avgCost: 165.0, currency: "USD", notes: "" },
  { ticker: "NVDA",  name: "NVIDIA Corp.",   market: "US", side: "Long",  quantity: 50,  avgCost: 420.0, currency: "USD", notes: "" },
  { ticker: "TSLA",  name: "Tesla Inc.",     market: "US", side: "Short", quantity: 30,  avgCost: 250.0, currency: "USD", notes: "밸류에이션 숏" },
  { ticker: "7203",  name: "Toyota Motor",   market: "JP", side: "Long",  quantity: 200, avgCost: 2800,  currency: "JPY", notes: "" },
  { ticker: "9984",  name: "SoftBank Group", market: "JP", side: "Short", quantity: 100, avgCost: 9000,  currency: "JPY", notes: "" },
];

export default function App() {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sheetsError, setSheetsError] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 1024);
  const [activeTab, setActiveTab] = useState("portfolio"); // "portfolio" | "trades"
  const [lsCorr, setLsCorr] = useState(null); // 롱·숏 바스켓 상관 (CorrelationPanel→PortfolioComment 공유)
  const [quickPair, setQuickPair] = useState(null); // 보유종목 옆 페어 버튼 앵커
  const [nav, setNav] = useState(() => (typeof window !== "undefined" ? Number(localStorage.getItem("ls_nav")) || 0 : 0));
  const [pairs, setPairs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ls_pairs") || "[]").map(normalizePair); } catch { return []; }
  });
  const updateNav = (v) => { const n = Number(v) || 0; setNav(n); try { localStorage.setItem("ls_nav", String(n)); } catch {} };
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const { marketData, exchangeRates, loading: mktLoading, lastUpdated, error: mktError, refresh, retryFailed } = useMarketData();

  // Google Sheets에서 포트폴리오 불러오기
  const loadPortfolio = useCallback(async () => {
    if (!IS_CONFIGURED) {
      // 설정 전이면 demo 데이터 사용
      setHoldings(DEMO_HOLDINGS);
      setLoading(false);
      return;
    }
    try {
      const res = await fetchPortfolio();
      if (res.holdings) {
        setHoldings(res.holdings);
      }
    } catch (err) {
      console.error("Sheets load error:", err);
      setSheetsError(true);
      setHoldings(DEMO_HOLDINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);

  // 시세 최초 로드
  useEffect(() => {
    if (holdings.length > 0) {
      refresh(holdings);
    }
  }, [holdings, refresh]);

  // 실패 종목만 재시도 (2초 간격, 최대 10회 후 새로고침 안내)
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY = 10;

  useEffect(() => {
    if (!marketData || Object.keys(marketData).length === 0) return;
    const failedHoldings = holdings.filter(h => !marketData[h.ticker]);
    if (failedHoldings.length === 0) { setRetryCount(0); return; }
    if (retryCount >= MAX_RETRY) return; // 10회 초과 시 멈춤

    const retry = setTimeout(async () => {
      await retryFailed(failedHoldings);
      setRetryCount(c => c + 1);
    }, 800);
    return () => clearTimeout(retry);
  }, [marketData, holdings, retryCount]);

  // Sheets 저장 헬퍼
  const persistHoldings = async (newHoldings) => {
    if (!IS_CONFIGURED) return;
    try {
      await savePortfolio(newHoldings);
    } catch (err) {
      console.error("Sheets save error:", err);
    }
  };

  const handleAdd = async holding => {
    const newHoldings = [...holdings, holding];
    setHoldings(newHoldings);
    if (IS_CONFIGURED) await addHolding(holding);
    refresh(newHoldings);
  };

  const handleBulkAdd = async (newItems, replace = false) => {
    let merged;
    if (replace) {
      merged = newItems;                 // 계좌 스냅샷 통째 교체(기존 전체 삭제)
    } else {
      merged = [...holdings];
      newItems.forEach(item => {
        const idx = merged.findIndex(h => h.ticker === item.ticker);
        if (idx >= 0) merged[idx] = item;
        else merged.push(item);
      });
    }
    setHoldings(merged);
    await persistHoldings(merged);
    refresh(merged);
  };

  const handleUpdate = async holding => {
    const { _oldTicker, ...clean } = holding;
    const oldTicker = _oldTicker ?? clean.ticker;
    const tickerChanged = String(oldTicker) !== String(clean.ticker);
    const newHoldings = holdings.map(h => h.ticker === oldTicker ? clean : h);
    setHoldings(newHoldings);
    if (IS_CONFIGURED) {
      if (tickerChanged) { await deleteHolding(oldTicker); await addHolding(clean); }
      else await updateHolding(clean);
    }
    // ticker 변경 시 새 코드로 시세 재조회 필요
    if (tickerChanged) refresh(newHoldings);
  };

  const persistPairs = next => { setPairs(next); try { localStorage.setItem("ls_pairs", JSON.stringify(next)); } catch {} };
  const handleAddPair = pair => { const np = normalizePair(pair); if (!pairs.some(p => samePair(p, np))) persistPairs([...pairs, np]); };
  const handleUpdatePair = (oldPair, newPair) => { const np = normalizePair(newPair); persistPairs(pairs.map(p => (samePair(p, oldPair) ? np : p))); };
  // 코멘트의 페어 아이디어 클릭 → 차트북에 추가 후 해당 카드로 스크롤
  const handleOpenPair = pair => {
    const np = normalizePair(pair);
    handleAddPair(np);
    setTimeout(() => {
      const el = document.getElementById(`pair-${pairId(np)}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  };
  const handleDeletePair = pair => persistPairs(pairs.filter(p => !samePair(p, pair)));

  const handleDelete = async ticker => {
    if (!window.confirm(`${ticker}를 삭제하시겠습니까?`)) return;
    const newHoldings = holdings.filter(h => h.ticker !== ticker);
    setHoldings(newHoldings);
    if (IS_CONFIGURED) await deleteHolding(ticker);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f5f5f7",
      fontFamily: "'DM Sans', 'Pretendard', -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: "#1a1a2e",
        color: "#fff",
        padding: "0 32px",
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em" }}>Long/Short</span>
          <span style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 4,
            background: "rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.6)",
          }}>KR·US·JP</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <input
            type="number" value={nav || ""} onChange={(e) => updateNav(e.target.value)}
            placeholder="NAV (₩)" title="펀드 자본금(NAV) — 노출%·한도 기준"
            style={{
              width: 130, padding: "5px 10px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)",
              color: "#fff", fontSize: 12,
            }}
          />
          {lastUpdated && (
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
              업데이트: {lastUpdated.toLocaleTimeString("ko-KR")}
            </span>
          )}
          <button
            onClick={() => refresh(holdings)}
            disabled={mktLoading}
            style={{
              padding: "5px 12px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.8)",
              fontSize: 12, cursor: "pointer",
            }}
          >
            {mktLoading ? "⏳ 로딩..." : "🔄 새로고침"}
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: "6px 14px", borderRadius: 6,
              border: "none", background: "#4a90d9",
              color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}
          >
            + 종목 추가
          </button>
        </div>
      </div>

      {/* 설정 안내 배너 */}
      {!IS_CONFIGURED && (
        <div style={{
          background: "#fff9e6",
          border: "1px solid #ffd666",
          padding: "10px 32px",
          fontSize: 13,
          color: "#7c6200",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <span>⚙️</span>
          <span>
            <strong>데모 모드</strong> — Google Apps Script를 배포하고{" "}
            <code style={{ background: "#fff3b0", padding: "1px 4px", borderRadius: 3 }}>.env</code> 파일에{" "}
            <code style={{ background: "#fff3b0", padding: "1px 4px", borderRadius: 3 }}>REACT_APP_SHEETS_URL</code>을 설정하면
            Google Sheets와 연동됩니다. 지금은 데모 데이터가 표시됩니다.
          </span>
        </div>
      )}

      {sheetsError && (
        <div style={{
          background: "#fff0f0",
          border: "1px solid #ffc0c0",
          padding: "10px 32px",
          fontSize: 13,
          color: "#c0392b",
        }}>
          ⚠️ Google Sheets 연결 실패 — 데모 데이터를 표시합니다. URL 설정을 확인해주세요.
        </div>
      )}

      {mktError && (
        <div style={{
          background: "#fff0f0",
          border: "1px solid #ffc0c0",
          padding: "10px 32px",
          fontSize: 13,
          color: "#c0392b",
        }}>
          ⚠️ {mktError}
        </div>
      )}

      {/* 시세 로딩 오버레이 — 포트폴리오 탭에서 모든 종목 로드 전까지 표시 */}
      {(() => {
        if (activeTab !== "portfolio") return null;
        const allLoaded = holdings.every(h => marketData[h.ticker]) || retryCount >= MAX_RETRY;
        return (loading || mktLoading || !allLoaded) && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(26,26,46,0.92)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 20,
        }}>
          <div style={{ fontSize: 48 }}>📊</div>
          <div style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>
            시세 불러오는 중...
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
            {holdings.length > 0
              ? `${Object.keys(marketData).length} / ${holdings.length}개 로드됨`
              : "포트폴리오 불러오는 중..."}
          </div>
          <div style={{
            width: 240, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 2, background: "#4a90d9",
              width: holdings.length > 0
                ? `${(Object.keys(marketData).length / holdings.length) * 100}%`
                : "0%",
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>
        );
      })()}

      {/* Main Content */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: isMobile ? "16px 12px" : "24px 32px", overflowX: "hidden" }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid #e8e8e8" }}>
          {[
            { key: "portfolio",   label: "📊 포트폴리오" },
            { key: "performance", label: "📈 성과"      },
            { key: "flows",       label: "📊 수급"      },
            { key: "trades",      label: "📝 매매내역"  },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: "10px 18px",
                background: "transparent",
                border: "none",
                borderBottom: activeTab === tab.key ? "2px solid #1a1a2e" : "2px solid transparent",
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? "#1a1a2e" : "#888",
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "portfolio" && (() => {
          const allLoaded = holdings.every(h => marketData[h.ticker]) || retryCount >= MAX_RETRY;
          return (!loading && allLoaded) && (
          <>
            {/* Summary Cards */}
            <SummaryCards holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} nav={nav} />

            {/* 국내 코스피/코스닥 노출 비중 */}
            <KrMarketSplit holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} />

            {/* 종합 방향성 코멘트 (제안형) */}
            <PortfolioComment holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} nav={nav} corr={lsCorr} onOpenPair={handleOpenPair} />

            {/* KR 시장 레짐 게이지 (모멘텀장 vs 회귀장) */}
            <RegimeGauge />

            {/* 롱·숏 바스켓 상관 */}
            <CorrelationPanel holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} onCorr={setLsCorr} />

            {/* 주요 뉴스 (보드 위) */}
            <NewsPanel holdings={holdings} />

            {/* 보유종목 한눈에 — 롱(좌)·숏(우) */}
            <LongShortBoard holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} nav={nav} isMobile={isMobile} onPairChart={setQuickPair} pairs={pairs} onAddPair={handleAddPair} onDeletePair={handleDeletePair} onUpdatePair={handleUpdatePair} onReorderPairs={persistPairs} />

            {/* Chart + Table 레이아웃 */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "300px 1fr", gap: 20, marginBottom: 24 }}>
              <div>
                <AllocationChart holdings={holdings} marketData={marketData} />
                <SectorExposure holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} />
              </div>
              <div style={{ minWidth: 0 }}>
                <PortfolioTable
                  holdings={holdings}
                  marketData={marketData}
                  exchangeRates={exchangeRates}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              </div>
            </div>

            {/* 복기 아이디어 — 롱/숏 페어를 시작일과 함께 넣어두고 구간 수익률 추적 */}
            <PaperPortfolio />

            {/* Simulation */}
            <SimulationPanel
              holdings={holdings}
              marketData={marketData}
              exchangeRates={exchangeRates}
            />

            {/* 페어 스프레드 차트북 */}
            <PairSpreadBook
              pairs={pairs}
              holdings={holdings}
              onAddPair={handleAddPair}
              onDeletePair={handleDeletePair}
            />
          </>
          );
        })()}

        {activeTab === "performance" && (
          <PerformancePanel holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} />
        )}

        {activeTab === "flows" && <FlowsTab />}

        {activeTab === "trades" && <TradesPanel />}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <AddHoldingModal
          onAdd={handleAdd}
          onBulkAdd={handleBulkAdd}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* 보유종목 → 빠른 롱숏 페어 차트 */}
      {quickPair && (
        <QuickPairModal
          anchor={quickPair}
          onClose={() => setQuickPair(null)}
          onSavePair={p => { handleOpenPair(p); setQuickPair(null); }}
        />
      )}
    </div>
  );
}
